import { useEffect, useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import {
  ShieldCheckIcon,
  DocumentCheckIcon,
  CheckIcon,
  CpuChipIcon,
  BuildingLibraryIcon,
  ClockIcon,
  GlobeAltIcon,
  ScaleIcon,
  UserGroupIcon,
  AcademicCapIcon,
  SparklesIcon,
  DocumentMagnifyingGlassIcon,
  ChatBubbleLeftRightIcon,
} from '@heroicons/react/24/outline';

/* ─── Animation Helpers ─── */
const ease = [0.44, 0, 0.56, 1] as const;

function FadeUp({ children, delay = 0, className = '' }: { children: React.ReactNode; delay?: number; className?: string }) {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-60px' });
  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 40 }}
      animate={isInView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.8, delay, ease }}
      className={className}
    >
      {children}
    </motion.div>
  );
}

function FadeIn({ children, delay = 0, className = '' }: { children: React.ReactNode; delay?: number; className?: string }) {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-40px' });
  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0 }}
      animate={isInView ? { opacity: 1 } : {}}
      transition={{ duration: 0.7, delay, ease }}
      className={className}
    >
      {children}
    </motion.div>
  );
}

function ScaleIn({ children, delay = 0, className = '' }: { children: React.ReactNode; delay?: number; className?: string }) {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-40px' });
  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, scale: 0.95 }}
      animate={isInView ? { opacity: 1, scale: 1 } : {}}
      transition={{ duration: 0.6, delay, ease }}
      className={className}
    >
      {children}
    </motion.div>
  );
}

/* ─── MBA Accent Color — warm gold ─── */
const MBA_GOLD = '#C8922A';
const MBA_GOLD_LIGHT = '#F5E6C8';

/* ─── Data ─── */
const capabilities = [
  {
    icon: ShieldCheckIcon,
    title: 'Marketing Compliance',
    description:
      'Upload any ad, email, web page, or social post — image, PDF, or text — and receive a comprehensive compliance analysis in real time. 27 rules across FDIC Part 328, NCUA Part 740, Reg Z, Reg DD, ECOA, Fair Housing Act, and UDAAP. Every finding includes the specific regulation, severity, and plain-English remediation guidance.',
  },
  {
    icon: DocumentCheckIcon,
    title: 'HMDA LAR Builder',
    description:
      'A full HMDA pipeline: map source system fields, auto-populate derived data, run 160+ edit checks with plain-English explanations, and export corrected LAR files ready for filing. Supports field-level overrides, gap analysis, geocoding validation, and APOR rate spread calculations.',
  },
  {
    icon: ScaleIcon,
    title: 'Fair Lending Analysis',
    description:
      'IFLEP-aligned regression analysis for denial rates, pricing disparities, and geographic lending patterns. Logistic and OLS regression models produce odds ratios, confidence intervals, and basis-point impacts — formatted the way examiners expect to see them. Includes redlining risk assessment and loan officer monitoring.',
  },
  {
    icon: GlobeAltIcon,
    title: 'Regulatory Intelligence',
    description:
      'Continuous monitoring of CFPB, OCC, FDIC, NCUA, FRB, and state regulators. Every regulatory update is categorized by compliance area, scored by priority, and linked to automatic action items. Your team knows what changed, what it means, and what to do — before the next exam cycle.',
  },
];

const differentiators = [
  {
    icon: UserGroupIcon,
    title: 'Built by a Community Banker',
    description:
      "Clarid's founder is a community bank CMO who manages marketing compliance, HMDA reporting, and examiner relationships as part of his daily work. This platform was built by someone who lives the compliance challenges MBA's members face every day.",
  },
  {
    icon: AcademicCapIcon,
    title: 'Examiner-Advised',
    description:
      'A former FDIC Supervisory Examiner and a former Federal Reserve Supervisory Examiner and fair lending subject matter expert advise Clarid\'s development. Every rule engine, every workflow, every output is validated against what examiners actually evaluate during safety and soundness and compliance examinations.',
  },
  {
    icon: CpuChipIcon,
    title: 'AI-Native Architecture',
    description:
      'Three foundation model providers with automatic failover and circuit breaker patterns. AI detects — it never determines. A deterministic rules engine makes every compliance decision reproducibly. Every interaction creates an immutable, SHA-256 hash-chained audit record that examiners can independently verify.',
  },
];

const partnershipElements = [
  {
    icon: SparklesIcon,
    title: 'Member Benefit Program',
    description:
      "Offer Clarid to MBA member banks at exclusive pricing. Banks get access to AI-powered compliance technology at a fraction of what they'd pay for consultants or legacy platforms. MBA delivers tangible, measurable value that members can point to at their next exam.",
  },
  {
    icon: AcademicCapIcon,
    title: 'Education & Co-Programming',
    description:
      "Co-host webinars, conference sessions, and training workshops on AI compliance, HMDA best practices, and exam readiness. Clarid's examiner advisors are available for joint educational programming — giving MBA members direct access to the examiner perspective.",
  },
  {
    icon: BuildingLibraryIcon,
    title: 'Compliance Resource',
    description:
      "Position Clarid as MBA's recommended compliance technology solution. Featured in member communications, conference programming, and the MBA resource directory. Banks trust MBA's endorsement — and Clarid's examiner-backed platform earns that trust.",
  },
];

const memberImpact = [
  {
    headline: 'Minutes, not days.',
    description:
      'First compliance check runs in minutes from signup. No IT department required. No implementation project. Your compliance team is up and running the same day.',
  },
  {
    headline: 'Exam-ready, always.',
    description:
      'Every compliance action is logged, timestamped, and exportable. When the examiner asks for documentation, it\'s one click — not two weeks of scrambling.',
  },
  {
    headline: 'Examiner-validated.',
    description:
      'Built with direct input from former FDIC and Federal Reserve examiners. The platform reflects what examiners actually evaluate — not what software companies assume they evaluate.',
  },
];

const nextSteps = [
  { num: '01', title: 'This Conversation', description: 'Align on mutual interest and explore partnership structure' },
  { num: '02', title: 'Member Needs Assessment', description: "Identify which compliance pain points resonate most with MBA's membership" },
  { num: '03', title: 'Pilot Group', description: 'Select 5–10 Missouri community banks for an exclusive early access program' },
  { num: '04', title: 'MBA Launch', description: 'Roll out to the full membership with co-branded materials and educational programming' },
];

/* ─── Component ─── */
export default function MBAPartnerPage() {
  useEffect(() => {
    document.title = 'Clarid + Missouri Bankers Association — Exam-Ready Compliance Tools';

    const metaTags: Record<string, string> = {
      'og:title': 'Clarid + Missouri Bankers Association — Exam-Ready Compliance Tools',
      'og:description': 'AI-native compliance tools built by a community banker, advised by former FDIC and Federal Reserve examiners. Purpose-built for Missouri\'s community banks.',
      'og:type': 'website',
      'og:image': '/clarid-logo.png',
      'twitter:card': 'summary_large_image',
      'twitter:title': 'Clarid + MBA Partnership',
      'twitter:description': 'Exam-ready compliance technology for Missouri\'s community banks.',
    };

    const existingTags: HTMLMetaElement[] = [];
    for (const [property, content] of Object.entries(metaTags)) {
      const meta = document.createElement('meta');
      meta.setAttribute('property', property);
      meta.setAttribute('content', content);
      document.head.appendChild(meta);
      existingTags.push(meta);
    }

    return () => {
      existingTags.forEach((tag) => document.head.removeChild(tag));
      document.title = 'Clarid — Clarity in every communication';
    };
  }, []);

  return (
    <div className="min-h-screen bg-white font-body text-slate-700">
      {/* ─── Minimal Header ─── */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-white/80 backdrop-blur-md border-b border-slate-100">
        <div className="max-w-[90rem] mx-auto px-8 lg:px-12 py-5 flex items-center">
          <img src="/clarid-logo.png" alt="Clarid" className="h-10" />
        </div>
      </header>

      {/* ─── Section 1: Hero ─── */}
      <section className="relative pt-36 pb-24 md:pt-44 md:pb-32 lg:pt-48 lg:pb-36 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-navy-900 via-navy-800 to-navy-900" />
        <div className="absolute inset-0 opacity-[0.04]" style={{
          backgroundImage: 'radial-gradient(circle at 1px 1px, white 1px, transparent 0)',
          backgroundSize: '40px 40px',
        }} />
        {/* Gradient orb */}
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] rounded-full opacity-15"
          style={{ background: `radial-gradient(circle, ${MBA_GOLD}40, transparent 70%)` }}
        />

        <div className="relative max-w-[90rem] mx-auto px-8 lg:px-12 text-center">
          <FadeUp>
            <div className="inline-flex items-center gap-2.5 px-5 py-2 rounded-full bg-white/10 backdrop-blur-sm border border-white/10 mb-10">
              <span className="h-2.5 w-2.5 rounded-full animate-pulse" style={{ backgroundColor: MBA_GOLD }} />
              <span className="text-base text-teal-200 font-medium">Partnership Opportunity</span>
            </div>
          </FadeUp>

          <FadeUp delay={0.1}>
            <h1 className="text-5xl md:text-6xl lg:text-7xl xl:text-[5rem] font-display font-bold text-white leading-[1.1] mb-8 max-w-5xl mx-auto">
              Exam-Ready Compliance Tools for{' '}
              <span style={{ color: MBA_GOLD }}>Missouri's Community Banks</span>
            </h1>
          </FadeUp>

          <FadeUp delay={0.2}>
            <p className="text-xl md:text-2xl text-slate-300 max-w-4xl mx-auto leading-relaxed">
              Clarid is an AI-native compliance operating system — built by a community bank CMO,
              advised by a former FDIC Supervisory Examiner and a former Federal Reserve fair lending
              subject matter expert, and designed to help community banks manage compliance with confidence.
            </p>
          </FadeUp>
        </div>
      </section>

      {/* ─── Section 2: The Problem ─── */}
      <section className="py-24 md:py-32 bg-slate-50">
        <div className="max-w-[90rem] mx-auto px-8 lg:px-12">
          <div className="max-w-4xl mx-auto">
            <FadeUp>
              <div className="text-center mb-14">
                <p className="text-base font-semibold tracking-wider uppercase mb-4" style={{ color: MBA_GOLD }}>
                  The Challenge
                </p>
                <h2 className="text-4xl md:text-5xl font-display font-bold text-navy-900">
                  AI Is Changing How Banks Work. Compliance Hasn't Caught Up.
                </h2>
              </div>
            </FadeUp>

            <FadeUp delay={0.1}>
              <div className="space-y-7">
                <p className="text-lg md:text-xl text-slate-600 leading-relaxed">
                  Missouri's community banks are adopting AI tools faster than compliance frameworks can keep up.
                  Microsoft Copilot is generating marketing content. AI-assisted tools are touching lending workflows,
                  customer communications, and document creation. Every new capability creates new compliance exposure.
                </p>
                <p className="text-lg md:text-xl text-slate-600 leading-relaxed">
                  At the same time, regulatory expectations around HMDA data quality, fair lending monitoring, and marketing
                  compliance continue to increase. Examiners expect continuous processes, not point-in-time reviews.
                </p>
                <p className="text-lg md:text-xl text-slate-600 leading-relaxed">
                  Most community banks have 1–3 people handling compliance across the entire institution. They're managing
                  with spreadsheets, manual review, and institutional knowledge that walks out the door when someone retires.
                </p>
              </div>
            </FadeUp>

            <ScaleIn delay={0.2}>
              <div className="mt-10 bg-navy-900 rounded-2xl p-10 md:p-12 text-center">
                <p className="text-xl md:text-2xl text-slate-300 leading-relaxed">
                  Banks need technology that applies compliance intelligence in real time — inside the tools
                  their teams already use — with{' '}
                  <span className="text-white font-semibold">audit trails that meet examiner expectations</span>.
                </p>
              </div>
            </ScaleIn>
          </div>
        </div>
      </section>

      {/* ─── Section 3: What Clarid Does ─── */}
      <section id="what-clarid-does" className="py-24 md:py-32">
        <div className="max-w-[90rem] mx-auto px-8 lg:px-12">
          <FadeUp>
            <div className="text-center mb-16">
              <p className="text-base font-semibold text-teal-500 tracking-wider uppercase mb-4">
                The Platform
              </p>
              <h2 className="text-4xl md:text-5xl font-display font-bold text-navy-900 mb-5">
                AI-Native Compliance Tools for the Banks MBA Serves
              </h2>
              <p className="text-xl text-slate-500 max-w-4xl mx-auto leading-relaxed">
                Purpose-built for community bank compliance teams. Every feature designed around how examiners
                actually evaluate institutions.
              </p>
            </div>
          </FadeUp>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 lg:gap-10">
            {capabilities.map((cap, i) => (
              <ScaleIn key={cap.title} delay={i * 0.1}>
                <div className="group relative p-10 bg-white rounded-2xl border border-slate-100 hover:border-teal-200 hover:shadow-xl transition-all duration-500 h-full">
                  <div className="w-14 h-14 rounded-xl bg-teal-50 flex items-center justify-center mb-6 group-hover:bg-teal-100 group-hover:scale-110 transition-all duration-300">
                    <cap.icon className="h-7 w-7 text-teal-600" />
                  </div>
                  <h3 className="text-2xl font-display font-semibold text-navy-900 mb-4">{cap.title}</h3>
                  <p className="text-lg text-slate-500 leading-relaxed">{cap.description}</p>
                </div>
              </ScaleIn>
            ))}
          </div>

          {/* MCP Integration callout */}
          <FadeUp delay={0.4}>
            <div className="mt-14 flex items-center justify-center">
              <div className="inline-flex items-center gap-3 px-8 py-4 rounded-full bg-slate-50 border border-slate-200">
                <ChatBubbleLeftRightIcon className="h-6 w-6 text-teal-500" />
                <span className="text-base text-slate-600">
                  <span className="font-semibold text-navy-900">Works inside the tools your team already uses</span>
                  {' '}— Microsoft Copilot, Claude, ChatGPT, and any MCP-compatible AI assistant
                </span>
              </div>
            </div>
          </FadeUp>
        </div>
      </section>

      {/* ─── Section 4: Why This Is Different ─── */}
      <section className="py-24 md:py-32 bg-navy-900 relative overflow-hidden">
        <div className="absolute inset-0 opacity-[0.03]" style={{
          backgroundImage: 'radial-gradient(circle at 1px 1px, white 1px, transparent 0)',
          backgroundSize: '48px 48px',
        }} />

        <div className="relative max-w-[90rem] mx-auto px-8 lg:px-12">
          <FadeUp>
            <div className="text-center mb-16">
              <p className="text-base font-semibold tracking-wider uppercase mb-4" style={{ color: MBA_GOLD }}>
                Why It Matters
              </p>
              <h2 className="text-4xl md:text-5xl font-display font-bold text-white mb-5">
                The Compliance Platform Examiners Helped Build
              </h2>
              <p className="text-xl text-slate-400 max-w-4xl mx-auto leading-relaxed">
                Not another generic RegTech product. A compliance operating system built from inside the community bank.
              </p>
            </div>
          </FadeUp>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 lg:gap-10">
            {differentiators.map((item, i) => (
              <ScaleIn key={item.title} delay={i * 0.12}>
                <div className="p-10 rounded-2xl bg-white/5 border border-white/10 backdrop-blur-sm hover:bg-white/[0.08] hover:border-white/20 transition-all duration-500 h-full">
                  <div className="w-14 h-14 rounded-xl flex items-center justify-center mb-6" style={{ backgroundColor: `${MBA_GOLD}20` }}>
                    <item.icon className="h-7 w-7" style={{ color: MBA_GOLD }} />
                  </div>
                  <h3 className="text-2xl font-display font-semibold text-white mb-4">{item.title}</h3>
                  <p className="text-lg text-slate-400 leading-relaxed">{item.description}</p>
                </div>
              </ScaleIn>
            ))}
          </div>
        </div>
      </section>

      {/* ─── Section 5: The MBA Partnership ─── */}
      <section className="py-24 md:py-32 bg-slate-50">
        <div className="max-w-[90rem] mx-auto px-8 lg:px-12">
          <FadeUp>
            <div className="text-center mb-16">
              <p className="text-base font-semibold text-teal-500 tracking-wider uppercase mb-4">
                Partnership
              </p>
              <h2 className="text-4xl md:text-5xl font-display font-bold text-navy-900 mb-5">
                What Clarid + MBA Looks Like
              </h2>
              <p className="text-xl text-slate-500 max-w-4xl mx-auto leading-relaxed">
                MBA exists to strengthen Missouri's community banks. Clarid exists to make compliance
                manageable for those same institutions. Here's how we see this working together.
              </p>
            </div>
          </FadeUp>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 lg:gap-10">
            {partnershipElements.map((el, i) => (
              <ScaleIn key={el.title} delay={i * 0.12}>
                <div className="p-10 bg-white rounded-2xl border border-slate-100 hover:shadow-xl transition-all duration-500 h-full">
                  <div className="w-14 h-14 rounded-xl flex items-center justify-center mb-6" style={{ backgroundColor: MBA_GOLD_LIGHT }}>
                    <el.icon className="h-7 w-7" style={{ color: MBA_GOLD }} />
                  </div>
                  <h3 className="text-2xl font-display font-semibold text-navy-900 mb-4">{el.title}</h3>
                  <p className="text-lg text-slate-500 leading-relaxed">{el.description}</p>
                </div>
              </ScaleIn>
            ))}
          </div>
        </div>
      </section>

      {/* ─── Section 6: Member Impact ─── */}
      <section className="py-24 md:py-32">
        <div className="max-w-[90rem] mx-auto px-8 lg:px-12">
          <FadeUp>
            <div className="text-center mb-16">
              <p className="text-base font-semibold tracking-wider uppercase mb-4" style={{ color: MBA_GOLD }}>
                Member Value
              </p>
              <h2 className="text-4xl md:text-5xl font-display font-bold text-navy-900">
                What MBA Members Get
              </h2>
            </div>
          </FadeUp>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 lg:gap-10">
            {memberImpact.map((item, i) => (
              <ScaleIn key={item.headline} delay={i * 0.12}>
                <div className="text-center p-10 bg-white rounded-2xl border border-slate-100 shadow-sm hover:shadow-xl transition-all duration-500 h-full">
                  <div className="w-16 h-16 rounded-full mx-auto mb-7 flex items-center justify-center" style={{ backgroundColor: MBA_GOLD_LIGHT }}>
                    {i === 0 && <ClockIcon className="h-8 w-8" style={{ color: MBA_GOLD }} />}
                    {i === 1 && <DocumentMagnifyingGlassIcon className="h-8 w-8" style={{ color: MBA_GOLD }} />}
                    {i === 2 && <CheckIcon className="h-8 w-8" style={{ color: MBA_GOLD }} />}
                  </div>
                  <h3 className="text-2xl md:text-3xl font-display font-bold text-navy-900 mb-4">{item.headline}</h3>
                  <p className="text-lg text-slate-500 leading-relaxed">{item.description}</p>
                </div>
              </ScaleIn>
            ))}
          </div>
        </div>
      </section>

      {/* ─── Section 7: Next Steps ─── */}
      <section className="py-24 md:py-32 bg-navy-900 relative overflow-hidden">
        <div className="absolute inset-0 opacity-[0.03]" style={{
          backgroundImage: 'radial-gradient(circle at 1px 1px, white 1px, transparent 0)',
          backgroundSize: '48px 48px',
        }} />

        <div className="relative max-w-[90rem] mx-auto px-8 lg:px-12">
          <div className="max-w-4xl mx-auto">
            <FadeUp>
              <div className="text-center mb-16">
                <p className="text-base font-semibold tracking-wider uppercase mb-4" style={{ color: MBA_GOLD }}>
                  Path Forward
                </p>
                <h2 className="text-4xl md:text-5xl font-display font-bold text-white">
                  How We Move Forward
                </h2>
              </div>
            </FadeUp>

            <div className="space-y-10">
              {nextSteps.map((step, i) => (
                <FadeUp key={step.num} delay={i * 0.12}>
                  <div className="flex items-start gap-7">
                    <div
                      className="flex-shrink-0 w-14 h-14 rounded-full flex items-center justify-center text-white text-lg font-bold"
                      style={{ backgroundColor: MBA_GOLD }}
                    >
                      {step.num}
                    </div>
                    <div className="flex-1 pt-2">
                      <h3 className="text-xl md:text-2xl font-display font-semibold text-white mb-2">{step.title}</h3>
                      <p className="text-lg text-slate-400 leading-relaxed">{step.description}</p>
                      {i < nextSteps.length - 1 && (
                        <div className="w-px h-8 bg-white/10 ml-0 mt-5" />
                      )}
                    </div>
                  </div>
                </FadeUp>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ─── Section 8: Contact ─── */}
      <section className="py-24 md:py-32">
        <div className="max-w-[90rem] mx-auto px-8 lg:px-12 text-center">
          <FadeIn>
            <div className="space-y-3">
              <p className="text-2xl md:text-3xl font-display font-bold text-navy-900">Bobby Sloan</p>
              <p className="text-lg text-slate-500">Founder & CEO, Clarid</p>
              <div className="flex items-center justify-center gap-5 mt-4 text-base text-slate-400">
                <a href="mailto:bobby@clarid.ai" className="hover:text-teal-600 transition-colors">bobby@clarid.ai</a>
                <span className="h-5 w-px bg-slate-200" />
                <span>clarid.ai</span>
              </div>
            </div>
          </FadeIn>
        </div>
      </section>

      {/* ─── Minimal Footer ─── */}
      <footer className="py-8 bg-white border-t border-slate-100">
        <div className="max-w-[90rem] mx-auto px-8 lg:px-12 flex flex-col sm:flex-row items-center justify-between gap-4">
          <img src="/clarid-logo.png" alt="Clarid" className="h-8 opacity-60" />
          <p className="text-slate-400 text-sm">
            &copy; {new Date().getFullYear()} Clarid&trade;. Confidential.
          </p>
        </div>
      </footer>
    </div>
  );
}
